import { useState, useRef, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { ErrorService } from '@/lib/errors/ErrorService';

/**
 * `starting` couvre l'attente du micro : entre le clic et l'ouverture effective
 * du périphérique, qui peut durer plusieurs secondes au premier usage (demande
 * d'autorisation, réveil d'un casque). L'interface s'en sert pour désactiver le
 * bouton micro pendant ce temps.
 */
export type VoiceRecordingState = 'idle' | 'starting' | 'recording' | 'processing';

interface VoiceRecordingOptions {
  context?: string;
  language?: string;
  additionalContext?: string;
  onTranscription?: (text: string) => void;
}

interface UseVoiceRecordingReturn {
  state: VoiceRecordingState;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  cancelRecording: () => void;
  recordingDuration: number;
  analyserNode: AnalyserNode | null;
}

/**
 * Tout ce qui appartient à UN enregistrement. Les morceaux audio vivent ici et
 * non dans une ref partagée par le hook : un enregistreur ne peut donc écrire
 * que dans son propre fichier.
 *
 * Incident du 2026-10-08 (dictée en échec, HTTP 502 `VOICE_TRANSCRIPTION_FAILED`) :
 * deux clics pendant l'ouverture du micro lançaient deux `MediaRecorder` qui
 * poussaient leurs morceaux dans le même tableau. Le second vidait le tableau,
 * donc l'en-tête webm du premier, puis les deux flux s'entrelaçaient : ffmpeg
 * refusait le fichier côté serveur. Le premier enregistreur, jamais arrêté,
 * continuait d'écrire dans les dictées suivantes jusqu'au rechargement de l'onglet.
 */
interface RecordingSession {
  stream: MediaStream | null;
  audioContext: AudioContext | null;
  recorder: MediaRecorder | null;
  chunks: Blob[];
  /** Vrai dès que l'enregistrement est abandonné (annulation, erreur, démontage) : rien ne doit partir au serveur. */
  cancelled: boolean;
}

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:5001';

/** Coupe le micro et libère le contexte audio d'un enregistrement. Sans effet si déjà fait. */
function releaseSessionResources(session: RecordingSession): void {
  if (session.stream) {
    session.stream.getTracks().forEach(track => track.stop());
    session.stream = null;
  }
  if (session.audioContext) {
    session.audioContext.close().catch(() => {});
    session.audioContext = null;
  }
}

export function useVoiceRecording(options: VoiceRecordingOptions = {}): UseVoiceRecordingReturn {
  const {
    context = 'case-discovery',
    language = 'fr',
    additionalContext,
    onTranscription,
  } = options;

  const [state, setState] = useState<VoiceRecordingState>('idle');
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [analyserNode, setAnalyserNode] = useState<AnalyserNode | null>(null);

  // Verrou de ré-entrée. Une ref et non `state` : deux clics dans le même tick
  // liraient tous deux `state === 'idle'`, la ref est à jour dès le premier.
  const sessionRef = useRef<RecordingSession | null>(null);
  const isSendingRef = useRef<boolean>(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  /** Termine un enregistrement : libère ses ressources et rend le verrou s'il le détient encore. */
  const endSession = useCallback((session: RecordingSession) => {
    releaseSessionResources(session);
    // Le minuteur et l'onde appartiennent à l'enregistrement COURANT. Le `onstop`
    // d'un enregistrement annulé arrive en différé : s'il les coupait sans cette
    // garde, il éteindrait ceux de l'enregistrement lancé entre-temps.
    if (sessionRef.current === session) {
      stopTimer();
      setAnalyserNode(null);
      sessionRef.current = null;
    }
  }, [stopTimer]);

  const sendAudioToAPI = useCallback(async (audioBlob: Blob) => {
    isSendingRef.current = true;
    setState('processing');

    try {
      // Get auth token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Non authentifié');
      }

      const formData = new FormData();
      // Use webm format since MediaRecorder produces webm by default
      formData.append('file', audioBlob, 'recording.webm');
      formData.append('context', context);
      formData.append('language', language);
      if (additionalContext) {
        formData.append('additionalContext', additionalContext);
      }

      const response = await fetch(`${BACKEND_URL}/api/voice-instructions/reformulate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          // Do NOT set Content-Type — let the browser handle multipart/form-data boundary
        },
        body: formData,
      });

      if (response.status === 429) {
        const errorData = await response.json();
        const retryAfterMs = errorData.retryAfterMs || 5000;
        toast.error('Service temporairement saturé', {
          description: `Réessayez dans ${Math.ceil(retryAfterMs / 1000)} secondes`,
        });
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erreur inconnue' }));
        throw new Error(errorData.error || `Erreur ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.instructions) {
        onTranscription?.(data.instructions);
      } else if (data.success && data.noInstructionDetected) {
        // Réponse normale du serveur, pas une panne : aucune parole exploitable dans
        // l'audio (silence, faux départ), ou texte écarté parce que plus long que ce
        // que la durée de l'audio permet de prononcer. Sans message dédié, l'avocat
        // voyait une erreur générique et concluait que la dictée était cassée.
        toast.error('Aucune parole détectée', {
          description: 'Vérifiez que votre micro capte bien votre voix, puis dictez à nouveau.',
        });
      } else {
        throw new Error(data.error || 'Pas de transcription reçue');
      }
    } catch (error: unknown) {
      ErrorService.handle(error, { context: { action: 'voice_transcription' } });
    } finally {
      isSendingRef.current = false;
      setState('idle');
    }
  }, [context, language, additionalContext, onTranscription]);

  const startRecording = useCallback(async () => {
    // Un seul enregistrement à la fois, de la demande de micro à la fin de l'envoi.
    if (sessionRef.current || isSendingRef.current) return;

    const session: RecordingSession = {
      stream: null,
      audioContext: null,
      recorder: null,
      chunks: [],
      cancelled: false,
    };
    sessionRef.current = session;
    setState('starting');

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        },
      });
      session.stream = stream;

      // Annulé ou démonté pendant l'ouverture du micro : on le referme aussitôt,
      // sinon il resterait allumé sans aucun bouton pour l'éteindre.
      if (session.cancelled) {
        releaseSessionResources(session);
        return;
      }

      // Create AudioContext + AnalyserNode for real-time waveform
      const audioContext = new AudioContext();
      session.audioContext = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.8;
      source.connect(analyser);
      setAnalyserNode(analyser);

      // Choose best available audio format
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : 'audio/mp4';

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      session.recorder = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        // Skip collecting data if recording was cancelled
        if (session.cancelled) return;
        if (event.data.size > 0) {
          session.chunks.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const shouldSend = !session.cancelled && session.chunks.length > 0;
        const wasCancelled = session.cancelled;
        endSession(session);
        if (shouldSend) {
          void sendAudioToAPI(new Blob(session.chunks, { type: mimeType }));
        } else if (!wasCancelled) {
          // Arrêt sans aucun morceau audio : rien à envoyer, on rend la main.
          setState('idle');
        }
      };

      mediaRecorder.onerror = () => {
        toast.error('Erreur lors de l\'enregistrement');
        session.cancelled = true;
        endSession(session);
        setRecordingDuration(0);
        setState('idle');
      };

      mediaRecorder.start(250); // collect data every 250ms
      setState('recording');
      startTimeRef.current = Date.now();
      setRecordingDuration(0);

      // Update duration timer
      timerRef.current = setInterval(() => {
        setRecordingDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 1000);
    } catch (error: unknown) {
      session.cancelled = true;
      endSession(session);
      ErrorService.handle(error, { context: { action: 'start_voice_recording' } });
      setState('idle');
    }
  }, [sendAudioToAPI, endSession]);

  const stopRecording = useCallback(() => {
    const session = sessionRef.current;
    if (session?.recorder && session.recorder.state === 'recording') {
      // `onstop` envoie l'audio puis libère le reste ; le micro, lui, se coupe tout de suite.
      session.recorder.stop();
      if (session.stream) {
        session.stream.getTracks().forEach(track => track.stop());
        session.stream = null;
      }
    }
  }, []);

  const cancelRecording = useCallback(() => {
    const session = sessionRef.current;
    if (session) {
      // Set cancelled flag BEFORE stopping — prevents onstop & ondataavailable from sending
      session.cancelled = true;
      if (session.recorder && session.recorder.state === 'recording') {
        session.recorder.stop();
      }
      endSession(session);
    }
    setRecordingDuration(0);
    setState('idle');
  }, [endSession]);

  // Démontage en cours d'enregistrement (changement de dossier, fermeture du
  // dock) : sans cet arrêt, l'enregistreur et le micro survivraient au composant.
  useEffect(() => {
    return () => {
      const session = sessionRef.current;
      if (!session) return;
      session.cancelled = true;
      if (session.recorder && session.recorder.state === 'recording') {
        session.recorder.stop();
      }
      releaseSessionResources(session);
      sessionRef.current = null;
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  return {
    state,
    startRecording,
    stopRecording,
    cancelRecording,
    recordingDuration,
    analyserNode,
  };
}
